import type { TaskPhoneFormatOptions, TaskUtils } from '@Core/lib/tasks/utils/types';
import { warnSdkUtilFallback } from '@Core/lib/tasks/utils/warn';

import { PhoneFormat as PhoneFormatEnum } from '@/types/enums';

// Avoid pulling libphonenumber metadata into every public page SDK bundle.

type SdkPhoneFormat = NonNullable<TaskPhoneFormatOptions['format']>;

const TWO_DIGIT_PHONE_COUNTRY_CODES = new Set([
  '20',
  '27',
  '30',
  '31',
  '32',
  '33',
  '34',
  '36',
  '39',
  '40',
  '41',
  '43',
  '44',
  '45',
  '46',
  '47',
  '48',
  '49',
  '51',
  '52',
  '53',
  '54',
  '55',
  '56',
  '57',
  '58',
  '60',
  '61',
  '62',
  '63',
  '64',
  '65',
  '66',
  '81',
  '82',
  '84',
  '86',
  '90',
  '91',
  '92',
  '93',
  '94',
  '95',
  '98'
]);

const THREE_DIGIT_PHONE_COUNTRY_CODES = new Set([
  '211',
  '212',
  '213',
  '216',
  '218',
  '220',
  '221',
  '222',
  '223',
  '224',
  '225',
  '226',
  '227',
  '228',
  '229',
  '230',
  '231',
  '232',
  '233',
  '234',
  '235',
  '236',
  '237',
  '238',
  '239',
  '240',
  '241',
  '242',
  '243',
  '244',
  '245',
  '246',
  '248',
  '249',
  '250',
  '251',
  '252',
  '253',
  '254',
  '255',
  '256',
  '257',
  '258',
  '260',
  '261',
  '262',
  '263',
  '264',
  '265',
  '266',
  '267',
  '268',
  '269',
  '290',
  '291',
  '297',
  '298',
  '299',
  '350',
  '351',
  '352',
  '353',
  '354',
  '355',
  '356',
  '357',
  '358',
  '359',
  '370',
  '371',
  '372',
  '373',
  '374',
  '375',
  '376',
  '377',
  '378',
  '380',
  '381',
  '382',
  '383',
  '385',
  '386',
  '387',
  '389',
  '420',
  '421',
  '423',
  '500',
  '501',
  '502',
  '503',
  '504',
  '505',
  '506',
  '507',
  '508',
  '509',
  '590',
  '591',
  '592',
  '593',
  '594',
  '595',
  '596',
  '597',
  '598',
  '599',
  '670',
  '672',
  '673',
  '674',
  '675',
  '676',
  '677',
  '678',
  '679',
  '680',
  '681',
  '682',
  '683',
  '685',
  '686',
  '687',
  '688',
  '689',
  '690',
  '691',
  '692',
  '850',
  '852',
  '853',
  '855',
  '856',
  '880',
  '886',
  '960',
  '961',
  '962',
  '963',
  '964',
  '965',
  '966',
  '967',
  '968',
  '970',
  '971',
  '972',
  '973',
  '974',
  '975',
  '976',
  '977',
  '992',
  '993',
  '994',
  '995',
  '996',
  '998'
]);

function isKnownPhoneCountryCode(countryCode: string): boolean {
  return (
    countryCode === '1' ||
    countryCode === '7' ||
    TWO_DIGIT_PHONE_COUNTRY_CODES.has(countryCode) ||
    THREE_DIGIT_PHONE_COUNTRY_CODES.has(countryCode)
  );
}

function normalizePhone(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  const phone = value
    .trim()
    .replace(/^00/, '+')
    .replace(/[\s().-]/g, '');

  return /^\+[1-9]\d{7,14}$/.test(phone) ? phone : '';
}

function splitPhone(phone: string): { countryCode: string; national: string } {
  const digits = phone.slice(1);
  const firstDigit = digits.slice(0, 1);
  const firstTwoDigits = digits.slice(0, 2);
  const countryCode =
    firstDigit === '1' || firstDigit === '7'
      ? firstDigit
      : TWO_DIGIT_PHONE_COUNTRY_CODES.has(firstTwoDigits)
        ? firstTwoDigits
        : digits.slice(0, 3);

  return { countryCode, national: digits.slice(countryCode.length) };
}

function groupPhoneDigits(value: string): string {
  return value.replace(/\d{1,4}/g, (part, offset) => (offset === 0 ? part : ` ${part}`));
}

function formatSdkPhone(
  value: unknown,
  format: SdkPhoneFormat = PhoneFormatEnum.International
): string | null {
  const phone = normalizePhone(value);

  if (!phone) {
    warnSdkUtilFallback('phone.format', 'The phone number is empty or invalid; returning null.');
    return null;
  }

  const { countryCode, national } = splitPhone(phone);

  if (!isKnownPhoneCountryCode(countryCode)) {
    warnSdkUtilFallback('phone.format', 'The phone number is invalid; returning null.');
    return null;
  }

  if (countryCode === '1' && national.length !== 10) {
    warnSdkUtilFallback('phone.format', 'The phone number is invalid; returning null.');
    return null;
  }

  if (format === PhoneFormatEnum.E164) {
    return phone;
  }

  if (format === PhoneFormatEnum.Rfc3966) {
    return `tel:${phone}`;
  }

  if (format === PhoneFormatEnum.National) {
    return countryCode === '1' && national.length === 10
      ? `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
      : groupPhoneDigits(national);
  }

  return `+${countryCode} ${groupPhoneDigits(national)}`;
}

export const phoneUtils: TaskUtils['phone'] = {
  format: (value, options) =>
    formatSdkPhone(value, options?.format ?? PhoneFormatEnum.International)
};
